import React, { useState, useEffect, useCallback } from "react";
import { Container, Form, Button, Spinner, Modal } from "react-bootstrap";
import { Link } from "react-router-dom";
import { FaEdit, FaTrashAlt, FaPlus } from "react-icons/fa";
import { simplePostCall } from "../../api/ApiServices";
import ApiConfig from "../../api/ApiConfig";
import { toast, ToastContainer } from "react-toastify";
import ScaleLoader from "react-spinners/ScaleLoader";
import { formatNumber } from "../../utils/formatNumber";
import "../../Styles/yellocalfcss/Liabilities.css";

/**
 * Liabilities — top-level list / hub page for all school liabilities.
 *
 * Liabilities are debts/obligations the school must pay later. They split into:
 *  - Current (paid within 1 year): salaries payable, supplier invoices, taxes
 *  - Non-Current (long-term): bank loans, vehicle financing
 *
 * This page lets the user see ALL liabilities in one register with aggregate
 * tiles and quick links to specialised sub-pages (Accounts Payable, Loans,
 * Salaries Payable). Backend endpoints listed in ApiConfig.liability* are
 * required.
 */
const Liabilities = () => {
  const customer_currency = (
    localStorage.getItem("customer_currency") || ""
  ).replace(/"/g, "");

  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [filters, setFilters] = useState({
    type: "", // current | non_current
    category: "", // accounts_payable | loan | salary | tax | utility | other
    status: "", // unpaid | partial | paid | overdue
    search: "",
  });

  // Create / edit modal state (basic generic liability entry)
  const [showModal, setShowModal] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({
    liability_name: "",
    liability_type: "current",
    liability_category: "accounts_payable",
    liability_amount: "",
    liability_amount_paid: "",
    liability_due_date: "",
    liability_status: "unpaid",
    liability_description: "",
  });
  const [saving, setSaving] = useState(false);

  const fetchLiabilities = useCallback(async () => {
    setLoading(true);
    try {
      const payload = {
        customer_id: Number(localStorage.getItem("customer_id")) || 1005,
        api_key: localStorage.getItem("api_key"),
        user_id: Number(localStorage.getItem("user_id")),
        language: "en",
        type: filters.type,
        category: filters.category,
        status: filters.status,
        search: filters.search,
      };
      const res = await simplePostCall(
        ApiConfig.liabilityList,
        JSON.stringify(payload),
      );
      if (res?.result && Array.isArray(res.data)) {
        setRows(res.data);
      } else {
        setRows([]);
      }
    } catch (err) {
      console.error("Liability list fetch failed:", err);
      setRows([]);
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    fetchLiabilities();
  }, [fetchLiabilities]);

  // Aggregate the totals for the summary tiles.
  const totals = rows.reduce(
    (acc, r) => {
      const amt = parseFloat(r.liability_amount) || 0;
      const paid = parseFloat(r.liability_amount_paid) || 0;
      const balance = amt - paid;
      acc.total += amt;
      acc.paid += paid;
      acc.outstanding += balance;
      if (r.liability_type === "non_current") acc.long_term += balance;
      else acc.short_term += balance;
      return acc;
    },
    { total: 0, paid: 0, outstanding: 0, short_term: 0, long_term: 0 },
  );

  const openCreateModal = () => {
    setEditing(null);
    setForm({
      liability_name: "",
      liability_type: "current",
      liability_category: "accounts_payable",
      liability_amount: "",
      liability_amount_paid: "",
      liability_due_date: "",
      liability_status: "unpaid",
      liability_description: "",
    });
    setShowModal(true);
  };

  const openEditModal = (row) => {
    setEditing(row);
    setForm({
      liability_name: row.liability_name || "",
      liability_type: row.liability_type || "current",
      liability_category: row.liability_category || "accounts_payable",
      liability_amount: row.liability_amount || "",
      liability_amount_paid: row.liability_amount_paid || "",
      liability_due_date: row.liability_due_date
        ? String(row.liability_due_date).slice(0, 10)
        : "",
      liability_status: row.liability_status || "unpaid",
      liability_description: row.liability_description || "",
    });
    setShowModal(true);
  };

  const handleFormChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  const handleSave = async () => {
    if (!form.liability_name || !form.liability_amount) {
      toast.warning("Name and amount are required.");
      return;
    }
    setSaving(true);
    try {
      const basePayload = {
        customer_id: Number(localStorage.getItem("customer_id")) || 1005,
        api_key: localStorage.getItem("api_key"),
        user_id: Number(localStorage.getItem("user_id")),
        language: "en",
        ...form,
        liability_amount: Number(form.liability_amount),
        liability_amount_paid: form.liability_amount_paid
          ? Number(form.liability_amount_paid)
          : 0,
      };
      const url = editing ? ApiConfig.liabilityUpdate : ApiConfig.liabilityCreate;
      const payload = editing
        ? { ...basePayload, liability_id: editing.liability_id }
        : basePayload;
      const res = await simplePostCall(url, JSON.stringify(payload));
      if (res?.result) {
        toast.success(editing ? "Liability updated." : "Liability created.");
        setShowModal(false);
        fetchLiabilities();
      } else {
        toast.error(res?.message || "Save failed.");
      }
    } catch (err) {
      console.error("Save liability error:", err);
      toast.error("An error occurred while saving.");
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (row) => {
    if (!window.confirm(`Delete liability "${row.liability_name}"?`)) return;
    try {
      const res = await simplePostCall(
        ApiConfig.liabilityDelete,
        JSON.stringify({
          customer_id: Number(localStorage.getItem("customer_id")) || 1005,
          api_key: localStorage.getItem("api_key"),
          user_id: Number(localStorage.getItem("user_id")),
          liability_id: row.liability_id,
        }),
      );
      if (res?.result) {
        toast.success("Liability deleted.");
        fetchLiabilities();
      } else {
        toast.error(res?.message || "Delete failed.");
      }
    } catch (err) {
      toast.error("An error occurred during delete.");
    }
  };

  const statusPill = (status) => {
    const s = String(status || "unpaid").toLowerCase();
    if (s === "paid") return "lib-status lib-status-paid";
    if (s === "partial" || s === "partially_paid") return "lib-status lib-status-partial";
    if (s === "overdue") return "lib-status lib-status-overdue";
    return "lib-status lib-status-unpaid";
  };

  const statusLabel = (s) =>
    ({ paid: "Paid", partial: "Partially Paid", partially_paid: "Partially Paid", overdue: "Overdue" }[s] ||
      "Unpaid");

  return (
    <div className="liabilities-page">
      <ToastContainer />
      <Container fluid>
        <div className="special-header panel-white-all d-flex justify-content-between align-items-center">
          <h4 className="page-title">Liabilities</h4>
          <Button
            onClick={openCreateModal}
            style={{ backgroundColor: "var(--color-primary)", border: "none" }}
          >
            <FaPlus className="me-1" /> Add Liability
          </Button>
        </div>

        {/* Summary tiles */}
        <div className="lib-summary-row">
          <div className="lib-tile lib-tile-total">
            <span className="lib-tile-label">Total Liabilities</span>
            <span className="lib-tile-value">
              {customer_currency} {formatNumber(totals.total)}
            </span>
          </div>
          <div className="lib-tile lib-tile-paid">
            <span className="lib-tile-label">Paid To Date</span>
            <span className="lib-tile-value">
              {customer_currency} {formatNumber(totals.paid)}
            </span>
          </div>
          <div className="lib-tile lib-tile-outstanding">
            <span className="lib-tile-label">Outstanding</span>
            <span className="lib-tile-value">
              {customer_currency} {formatNumber(totals.outstanding)}
            </span>
          </div>
          <div className="lib-tile lib-tile-short">
            <span className="lib-tile-label">Current (Short-Term)</span>
            <span className="lib-tile-value">
              {customer_currency} {formatNumber(totals.short_term)}
            </span>
          </div>
          <div className="lib-tile lib-tile-long">
            <span className="lib-tile-label">Non-Current (Long-Term)</span>
            <span className="lib-tile-value">
              {customer_currency} {formatNumber(totals.long_term)}
            </span>
          </div>
        </div>

        {/* Quick links to specialised sub-pages */}
        <div className="lib-quicklinks">
          <Link to="/accountant/liabilities/payables" className="lib-quicklink">
            <span className="lib-quicklink-title">Accounts Payable</span>
            <span className="lib-quicklink-sub">Supplier invoices owed</span>
          </Link>
          <Link to="/accountant/liabilities/loans" className="lib-quicklink">
            <span className="lib-quicklink-title">Loans</span>
            <span className="lib-quicklink-sub">Bank loans &amp; vehicle financing</span>
          </Link>
          <Link to="/accountant/liabilities/salaries" className="lib-quicklink">
            <span className="lib-quicklink-title">Salaries Payable</span>
            <span className="lib-quicklink-sub">Accrued unpaid staff salaries</span>
          </Link>
        </div>

        {/* Filters */}
        <div className="lib-filter-bar">
          <Form.Select
            value={filters.type}
            onChange={(e) => setFilters({ ...filters, type: e.target.value })}
          >
            <option value="">All Types</option>
            <option value="current">Current (Short-Term)</option>
            <option value="non_current">Non-Current (Long-Term)</option>
          </Form.Select>
          <Form.Select
            value={filters.category}
            onChange={(e) =>
              setFilters({ ...filters, category: e.target.value })
            }
          >
            <option value="">All Categories</option>
            <option value="accounts_payable">Accounts Payable</option>
            <option value="loan">Loan</option>
            <option value="salary">Salaries Payable</option>
            <option value="tax">Taxes Payable</option>
            <option value="utility">Utility Bill</option>
            <option value="other">Other</option>
          </Form.Select>
          <Form.Select
            value={filters.status}
            onChange={(e) => setFilters({ ...filters, status: e.target.value })}
          >
            <option value="">All Statuses</option>
            <option value="unpaid">Unpaid</option>
            <option value="partial">Partially Paid</option>
            <option value="paid">Paid</option>
            <option value="overdue">Overdue</option>
          </Form.Select>
          <Form.Control
            type="text"
            placeholder="Search by name or supplier"
            value={filters.search}
            onChange={(e) => setFilters({ ...filters, search: e.target.value })}
          />
          <Button
            onClick={fetchLiabilities}
            disabled={loading}
            style={{ backgroundColor: "var(--color-primary)", border: "none" }}
          >
            {loading ? "Searching..." : "Search"}
          </Button>
          <Button
            variant="secondary"
            onClick={() => {
              setFilters({ type: "", category: "", status: "", search: "" });
              setTimeout(fetchLiabilities, 0);
            }}
          >
            Clear
          </Button>
        </div>

        {/* Table */}
        <div className="lib-table-wrap">
          <table className="lib-table">
            <thead>
              <tr>
                <th>#</th>
                <th>Name / Description</th>
                <th>Category</th>
                <th>Type</th>
                <th>Amount</th>
                <th>Paid</th>
                <th>Balance</th>
                <th>Due Date</th>
                <th>Status</th>
                <th className="text-center">Actions</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td colSpan="10" className="text-center py-4">
                    <ScaleLoader color="#F4BB44" />
                  </td>
                </tr>
              ) : rows.length === 0 ? (
                <tr>
                  <td colSpan="10" className="text-center py-4 text-muted">
                    No liabilities recorded.
                  </td>
                </tr>
              ) : (
                rows.map((r, idx) => {
                  const amt = parseFloat(r.liability_amount) || 0;
                  const paid = parseFloat(r.liability_amount_paid) || 0;
                  const bal = amt - paid;
                  return (
                    <tr key={r.liability_id || idx}>
                      <td>{idx + 1}</td>
                      <td>
                        <div className="fw-semibold">{r.liability_name}</div>
                        {r.liability_description && (
                          <div className="text-muted" style={{ fontSize: 12 }}>
                            {r.liability_description}
                          </div>
                        )}
                      </td>
                      <td>{r.liability_category || "—"}</td>
                      <td>
                        {r.liability_type === "non_current"
                          ? "Long-Term"
                          : "Short-Term"}
                      </td>
                      <td>{customer_currency} {formatNumber(amt)}</td>
                      <td>{customer_currency} {formatNumber(paid)}</td>
                      <td className="fw-bold">
                        {customer_currency} {formatNumber(bal)}
                      </td>
                      <td>
                        {r.liability_due_date
                          ? new Date(r.liability_due_date).toLocaleDateString()
                          : "—"}
                      </td>
                      <td>
                        <span className={statusPill(r.liability_status)}>
                          {statusLabel(r.liability_status)}
                        </span>
                      </td>
                      <td className="text-center">
                        <button
                          className="btn btn-sm"
                          title="Edit"
                          onClick={() => openEditModal(r)}
                        >
                          <FaEdit />
                        </button>
                        <button
                          className="btn btn-sm"
                          title="Delete"
                          onClick={() => handleDelete(r)}
                        >
                          <FaTrashAlt color="red" />
                        </button>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      </Container>

      {/* Create / Edit modal */}
      <Modal
        show={showModal}
        onHide={() => !saving && setShowModal(false)}
        centered
        size="lg"
      >
        <Modal.Header closeButton>
          <Modal.Title>{editing ? "Edit Liability" : "Add Liability"}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <div className="row g-3">
            <div className="col-md-6">
              <Form.Group>
                <Form.Label>Name <span className="text-danger">*</span></Form.Label>
                <Form.Control
                  name="liability_name"
                  value={form.liability_name}
                  onChange={handleFormChange}
                  placeholder="e.g. Office supplies invoice #INV-9821"
                />
              </Form.Group>
            </div>
            <div className="col-md-3">
              <Form.Group>
                <Form.Label>Type</Form.Label>
                <Form.Select
                  name="liability_type"
                  value={form.liability_type}
                  onChange={handleFormChange}
                >
                  <option value="current">Current (Short-Term)</option>
                  <option value="non_current">Non-Current (Long-Term)</option>
                </Form.Select>
              </Form.Group>
            </div>
            <div className="col-md-3">
              <Form.Group>
                <Form.Label>Category</Form.Label>
                <Form.Select
                  name="liability_category"
                  value={form.liability_category}
                  onChange={handleFormChange}
                >
                  <option value="accounts_payable">Accounts Payable</option>
                  <option value="loan">Loan</option>
                  <option value="salary">Salaries Payable</option>
                  <option value="tax">Taxes Payable</option>
                  <option value="utility">Utility Bill</option>
                  <option value="other">Other</option>
                </Form.Select>
              </Form.Group>
            </div>

            <div className="col-md-4">
              <Form.Group>
                <Form.Label>Amount <span className="text-danger">*</span></Form.Label>
                <Form.Control
                  type="number"
                  step="0.01"
                  name="liability_amount"
                  value={form.liability_amount}
                  onChange={handleFormChange}
                />
              </Form.Group>
            </div>
            <div className="col-md-4">
              <Form.Group>
                <Form.Label>Amount Paid</Form.Label>
                <Form.Control
                  type="number"
                  step="0.01"
                  name="liability_amount_paid"
                  value={form.liability_amount_paid}
                  onChange={handleFormChange}
                />
              </Form.Group>
            </div>
            <div className="col-md-4">
              <Form.Group>
                <Form.Label>Due Date</Form.Label>
                <Form.Control
                  type="date"
                  name="liability_due_date"
                  value={form.liability_due_date}
                  onChange={handleFormChange}
                />
              </Form.Group>
            </div>

            <div className="col-md-4">
              <Form.Group>
                <Form.Label>Status</Form.Label>
                <Form.Select
                  name="liability_status"
                  value={form.liability_status}
                  onChange={handleFormChange}
                >
                  <option value="unpaid">Unpaid</option>
                  <option value="partial">Partially Paid</option>
                  <option value="paid">Paid</option>
                  <option value="overdue">Overdue</option>
                </Form.Select>
              </Form.Group>
            </div>

            <div className="col-md-12">
              <Form.Group>
                <Form.Label>Description / Notes</Form.Label>
                <Form.Control
                  as="textarea"
                  rows={2}
                  name="liability_description"
                  value={form.liability_description}
                  onChange={handleFormChange}
                  placeholder="Supplier name, loan reference, accrual context, etc."
                />
              </Form.Group>
            </div>
          </div>
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant="secondary"
            onClick={() => setShowModal(false)}
            disabled={saving}
          >
            Cancel
          </Button>
          <Button
            onClick={handleSave}
            disabled={saving}
            style={{ backgroundColor: "var(--color-primary)", border: "none" }}
          >
            {saving ? <Spinner size="sm" /> : editing ? "Save Changes" : "Create Liability"}
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default Liabilities;
